import { type PaletteKey, PixelCanvas } from '../lib';
import {
  type Brim,
  grid,
  HAT_FRAMES,
  HAT_RIBBON,
  HAT_Y,
  HATS,
  hatAt,
  type Part,
  RIBBON,
  type RibbonKey,
  stack,
  tipped,
} from './body';

/**
 * Headwear (ADR-0089): the sun hat in its palettes (body.ts), a conical straw hat, a durag and a Rajasthani turban.
 * A hat sits on the head a frame behind it and flies off when its gardener is taken out (ADR-0036); headwear tied on
 * (`worn`) is part of the head: it moves with it, takes the hair's place (it covers the head, so no hairstyle shows) and
 * stays on in the dead pose. Drawn with care from real headwear, open to every skin: never a costume.
 *
 * Rubric (`looks hat` sheet at 3× and 1:1, the Hat tab at three sizes, the game at 1280×720): conical S5 R5 P4 L4 C4 A4
 * F4, durag S4 R4 P5 L4 C4 A4 F4, turban S5 R5 P4 L4 C3 A4 F4. Still weak: the turban's bandhani is busy at 3× (cloth
 * at 1:1); the durag lying is a dark shape more than silk; the chin strap is quiet on the darkest skins.
 */
export interface Hat {
  worn: false;
  /** On the head, a frame behind it, per brim flex. */
  hat: Record<Brim, PixelCanvas>;
  /** The team's ribbon, relative to the hat's top-left (`RIBBON`'s placing). */
  ribbon: Record<RibbonKey, Part>;
  /** With the face, under the hat: a chin strap. */
  head: readonly Part[];
  /** On the dead pose's head: what of `head` stays on when the hat flies off (a set's beard, ADR-0112). */
  lying?: readonly Part[];
  /** On its own once knocked off (64×40): tumbling, then lying on the grass. */
  fly: readonly PixelCanvas[];
  rest: readonly PixelCanvas[];
}
export interface Worn {
  worn: true;
  /** In the hair's place: over the face, covering the head. */
  front: Part;
  /** Its tails in the ribbon's place (relative to `HAT_Y`), moving with the head. */
  ribbon: Record<RibbonKey, Part>;
  /** On the dead pose's head. */
  dead: Part;
  /** Over the whole face (a set's head, ADR-0112): the face's extras (glasses, shades, an earring) stay under it. */
  mask?: boolean;
  /** Pictured alone (the wardrobe), when `front` leaves the face showing through it: a helmet with its glass whole. */
  alone?: Part;
}
export type Headwear = Hat | Worn;

export const SUN_HAT: Hat = { worn: false, hat: HATS, ribbon: RIBBON, head: [], ...HAT_FRAMES };

const same = (c: PixelCanvas): Record<Brim, PixelCanvas> => ({ flat: c, up: c, down: c });

// --- The conical hat -------------------------------------------------------------------------------------------------

/**
 * The cone in the frame at rest (pixel centres): its rim an ellipse round (CX, RIM_Y), `RX` × `RY` (the view's squash),
 * its peak `PEAK` px above the rim's middle.
 */
const CONE = { cx: 32, rimY: 21, rx: 28, ry: 6, peak: 15 } as const;

/**
 * Where the pixel centred on (x, y) lies on the cone's surface facing us: `u` from the peak (0) to the rim (1), `a` its
 * angle round the axis (0 to the right, π/2 toward us); null off the cone. `flip`: upside down (the peak below).
 */
function onCone(x: number, y: number, flip = false): { u: number; a: number } | null {
  const nx = (x - CONE.cx) / CONE.rx;
  const ny = ((flip ? -1 : 1) * (y - CONE.rimY)) / CONE.ry;
  const h = CONE.peak / CONE.ry;
  // x = u cos a, y = -h (1 - u) + u sin a (in rim units): the nearer of the two surfaces is the smaller u.
  const v = ny + h;
  const disc = v * v - (h * h - 1) * nx * nx;
  if (disc < 0) return null;
  const u = (v * h - Math.sqrt(disc)) / (h * h - 1);
  if (u < 0 || u > 1) return null;
  const a = Math.atan2((v - h * u) / Math.max(u, 1e-6), nx / Math.max(u, 1e-6));
  return { u, a };
}

/** Rings round the cone (the bamboo hoops under the leaves), at these distances from the peak. */
const RINGS = [0.26, 0.47, 0.68];
/** The team's band near the rim (a thread wound round it). */
const BAND = [0.8, 0.87] as const;
/** The leaves' edges (ribs) every this many radians round it, staggered ring to ring like a weave. */
const RIB = Math.PI / 7;

/** The straw's tone round the cone: lit on the left, shaded on the right and back (light from the top-left). */
function straw(a: number): PaletteKey {
  const lit = -Math.cos(a) * 0.9 + Math.sin(a) * 0.3;
  return lit > 0.1 ? 'e' : lit > -0.55 ? 't' : lit > -0.85 ? 'U' : 'm';
}
const DARKER: Partial<Record<PaletteKey, PaletteKey>> = { e: 't', t: 'U', U: 'm', m: 'n', n: 'N' };
const LIGHTER: Partial<Record<PaletteKey, PaletteKey>> = { e: 'e', t: 'e', U: 't', m: 'U' };
const TEAM: Partial<Record<PaletteKey, PaletteKey>> = { e: 'b', t: 'b', U: 'B', m: 'B' };

/**
 * The conical hat as worn (64 × 26 from `HAT_Y`), or its outside upside down (`flip`, 64 × 40 from 3 px above
 * `HAT_Y`): palm-leaf straw in three tones round the cone, three hoops showing through as rings, the leaves' edges
 * between them staggered like a weave, the team's thread near the rim, a lit bamboo hoop at the rim over a dark lip, a
 * knot at the peak.
 */
function cone(flip = false): PixelCanvas {
  const c = new PixelCanvas(64, flip ? 40 : 26);
  const top = flip ? HAT_Y - 3 : HAT_Y;
  const at = (x: number, y: number) => onCone(x + 0.5, y + top + 0.5, flip);
  const ring = (u: number) => RINGS.filter((r) => u >= r).length;
  const stagger = (u: number) => (ring(u) % 2) * 0.5;
  const rib = (p: { u: number; a: number }) => Math.floor(p.a / RIB + stagger(p.u));
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const p = at(x, y);
      if (!p) continue;
      // Each leaf catches the light as one: its tone is its middle's, so the light turns leaf by leaf.
      const base = straw((rib(p) + 0.5 - stagger(p.u)) * RIB);
      let tone = base;
      const up = at(x, y - 1);
      const left = at(x - 1, y);
      if (up && ring(up.u) !== ring(p.u)) tone = DARKER[base]!;
      else if (left && p.u > RINGS[0]! && ring(left.u) === ring(p.u) && rib(left) !== rib(p)) tone = DARKER[base]!;
      if (p.u >= BAND[0] && p.u < BAND[1]) tone = TEAM[base]!;
      if (p.u > 0.955) tone = LIGHTER[base]!;
      if (p.u < 0.08) tone = base === 'e' ? 'm' : 'n';
      c.set(x, y, tone);
      // The rim's lip under its front edge.
      if (!flip && p.u > 0.9 && !at(x, y + 1) && Math.sin(p.a) > 0) c.set(x, y + 1, DARKER[DARKER[base]!]!);
    }
  }
  return c;
}

const CONICAL_HAT = cone();

/**
 * Upside down in its tumble (64 × 40): the hollow up, seen through the rim. Inside, the back wall runs down toward the
 * peak (hidden behind the front wall): lit on the right (it faces the light), shaded on the left and deeper down, the
 * hoops as lit rings, the front rim's shadow along it; under the rim the outside's lower half, its peak below.
 */
function coneInside(): PixelCanvas {
  const c = cone(true);
  const { cx, rx, ry, peak } = CONE;
  const rimY = CONE.rimY - (HAT_Y - 3);
  /** How deep (0 at the rim, 1 at the peak) the back wall is at (x, y), or null outside the rim. */
  const depth = (x: number, y: number): number | null => {
    if (((x - cx) / rx) ** 2 + ((y - rimY) / ry) ** 2 > 1) return null;
    for (let f = 0; f <= 1; f += 0.01) {
      const w = (1 - f) * rx;
      const dx = (x - cx) / w;
      if (Math.abs(dx) > 1) return f;
      if (rimY + peak * f - (1 - f) * ry * Math.sqrt(1 - dx * dx) >= y) return f;
    }
    return 1;
  };
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const f = depth(x + 0.5, y + 0.5);
      if (f === null) continue;
      const side = (x + 0.5 - cx) / rx;
      let tone: PaletteKey = side > 0.35 ? 't' : side > -0.3 ? 'U' : 'm';
      if (f > 0.45) tone = DARKER[tone]!;
      const above = depth(x + 0.5, y - 0.5);
      if (above !== null && RINGS.some((r) => above < 1 - r && f >= 1 - r)) tone = LIGHTER[tone]!;
      // The front rim's shadow inside, and the rim's lit hoop all round.
      if (depth(x + 0.5, y + 1.5) === null || depth(x + 0.5, y + 2.5) === null) tone = 'n';
      if (depth(x + 0.5, y - 0.5) === null || depth(x - 0.5, y + 0.5) === null || depth(x + 1.5, y + 0.5) === null)
        tone = side > -0.3 ? 'e' : 't';
      c.set(x, y, tone);
    }
  }
  return c;
}

/**
 * The chin strap, a cord of the hat's own straw (a team cord down the cheeks read as streaks on the face; the team is on
 * the hat's band and ribbon): from under the rim down the cheek in front of the ear and down the
 * far cheek, to the jaw (under the chin it is out of sight). With the head, so the hat's lag never pulls it off the
 * face; it starts well under the rim, which covers its top whatever the lag.
 */
const STRAP: Part = [
  grid(`
    U.........................
    U.........................
    U.........................
    U.........................
    U.........................
    U.........................
    U........................u
    U........................u
    U........................u
    U........................u
    U........................u
    U........................u
    U........................u
    UU.......................u
    .U.......................u
    .UU.....................u.
    ..uU...................uu.
    ...uu.................uu..
  `),
  21,
  26,
];

const CONICAL: Hat = {
  worn: false,
  hat: same(CONICAL_HAT),
  ribbon: RIBBON,
  head: [STRAP],
  fly: [hatAt(4, CONICAL_HAT), tipped(14, CONICAL_HAT), coneInside(), tipped(-14, CONICAL_HAT)],
  // Lying rim down, its ribbon trailing from under it.
  rest: [new PixelCanvas(64, 40).blit(HAT_RIBBON, 2, 21).blit(CONICAL_HAT, 0, 6)],
};

// --- Worn headwear ---------------------------------------------------------------------------------------------------

/**
 * A strap of cloth (a durag's tie, a turban's tail) down `path` (points in the frame, top to bottom or back to front),
 * `w` px across: its lit edge `c`, body `b`, shaded edge `B` (the team's, recoloured), its end cut on the slant.
 */
function strap(path: readonly (readonly [number, number])[], w: number, lying = false): Part {
  const pts: [number, number][] = [];
  for (let i = 0; i + 1 < path.length; i++) {
    const [ax, ay] = path[i]!;
    const [bx, by] = path[i + 1]!;
    const n = Math.max(1, Math.round(lying ? Math.abs(bx - ax) : Math.abs(by - ay)));
    for (let k = 0; k < n; k++) pts.push([ax + ((bx - ax) * k) / n, ay + ((by - ay) * k) / n]);
  }
  pts.push([...path[path.length - 1]!]);
  const xs = pts.map(([x]) => x);
  const ys = pts.map(([, y]) => y);
  const x0 = Math.floor(Math.min(...xs)) - 1;
  const y0 = Math.floor(Math.min(...ys)) - 1;
  const c = new PixelCanvas(Math.ceil(Math.max(...xs)) - x0 + w + 2, Math.ceil(Math.max(...ys)) - y0 + w + 2);
  for (const [i, [x, y]] of pts.entries()) {
    // The end cut on the slant.
    for (let d = i === pts.length - 1 ? 1 : 0; d < w; d++) {
      const tone: PaletteKey = d === 0 ? 'c' : d === w - 1 ? 'B' : 'b';
      if (lying) c.set(Math.round(x) - x0, Math.round(y) + d - y0, tone);
      else c.set(Math.round(x) + d - x0, Math.round(y) - y0, tone);
    }
  }
  return [c, x0, y0];
}

/**
 * The durag on the head (ADR-0089), over the face in the hair's place (at (2, 14)): black silk (navy black `1`, its
 * deepest shadow `k`) fitted over the round of the head; the seam from the middle of the forehead over the crown, a lit
 * ridge beside it; satin sheen in long streaks along the fabric on the lit side, a glint in the brightest (silk, not a
 * helmet's round highlight); the edge away from the light catching a little light back; the band across the forehead
 * just above the brows, its lit top rolled over a crease, round the head above the ear; the flap down the nape behind
 * the ear, a sheen and a fold down it. The ties are knotted at the back in the team's colour (`DURAG_TIES` hang from the
 * knot): they take the ribbon's place.
 */
const DURAG_FRONT: Part = [
  grid(`
    ......................222211111...................
    ...................2222111k1111111................
    ................22222232222k111111111.............
    ..............22222232221112k1111111111...........
    .............222222322211112k11111111111..........
    ............22222232221111112k1111111111k1........
    ...........222222332211111112k11111111111k1.......
    ..........22222243221111111112k1111111111kk1......
    .........222222432211111111112k11111111111kk1.....
    .........2222224322111111111112k1111111111kk1.....
    ........22222253221111111111111k11111111111kk1....
    ........22222243221111111111111k11111111111kk1....
    .......2222223322111111111111111k11111111111kkk...
    .......2222223222111111111111111k11111111111kk1...
    .......2222223222kkkkkk111111111k111111111kk111...
    .......222223222k111111kkkkkkkkkkkkkkkkkkk11111...
    .......2222232kk1333333111111111111111111111111...
    .......222223k113111111333222222111111111111k1....
    .......2221kk1231kkkkkk1111111111111111111kk......
    .......22kk11211k......kkkkkkkkkkkkkkkkkkk........
    .........11221kk..................................
    .....cbbB1111k....................................
    ....cbbbbB1kkk....................................
    ....cbBbbBkk1k....................................
    .....bbBB21k1k....................................
    .......1121k1k....................................
    .......1121k1k....................................
    .......1121k1k....................................
    .......1121k1k....................................
    .......1121k1k....................................
    .......1121k1k....................................
    .......1k21kk1....................................
    ........1kkk1.....................................
    .........111......................................
    ..................................................
  `),
  2,
  12,
];

type Path = readonly (readonly [number, number])[];
/** Two ties hanging from the knot (frame points), as a ribbon part (relative to `HAT_Y`). */
function ties(a: Path, b: Path, lying = false): Part {
  const [c, x, y] = stack(strap(b, 3, lying), strap(a, 3, lying));
  return [c, x, y - HAT_Y];
}

/** The durag's ties, in the ribbon's place: hanging down the back, swaying, streaming behind on the run. */
const DURAG_TIES: Record<RibbonKey, Part> = {
  hang: ties(
    [
      [5, 36],
      [4, 44],
      [4, 51],
    ],
    [
      [8, 37],
      [8, 44],
      [9, 49],
    ],
  ),
  hang2: ties(
    [
      [5, 36],
      [3, 44],
      [2, 50],
    ],
    [
      [8, 37],
      [7, 44],
      [7, 48],
    ],
  ),
  flowA: ties(
    [
      [7, 34],
      [2, 36],
      [-3, 37],
    ],
    [
      [7, 38],
      [2, 41],
      [-2, 44],
    ],
    true,
  ),
  flowB: ties(
    [
      [7, 34],
      [2, 35],
      [-3, 39],
    ],
    [
      [7, 38],
      [2, 40],
      [-2, 42],
    ],
    true,
  ),
  flowC: ties(
    [
      [7, 34],
      [2, 37],
      [-3, 36],
    ],
    [
      [7, 38],
      [2, 42],
      [-2, 41],
    ],
    true,
  ),
};

/**
 * The durag on the dead pose's head (seen from above, the crown to the right): the band down the forehead just above
 * the shut eyes, the seam along the head's middle to the crown, the sheen on the lit upper side, the flap spread on
 * the grass past the crown and the ties fallen above it.
 */
const DURAG_DEAD: Part = [
  grid(`
    .........................
    .....................cbB.
    .....................cbB.
    ....................cbB..
    ........1111........cbB.c
    .......1111k1......cbB.cb
    ......k121k22221...cbB.cb
    .....k121k22222221cbB.cbB
    .....k131k222222221bBcbB.
    ....k131k23334333321.cbB.
    ....k131k334444433321111.
    ...k131k22222222222211111
    ...k131k222222222222k1211
    ...k131k111111111111k1211
    ..k121k1111111111111k12k1
    ..k121k1222222222221k12k1
    ..k121kkkkkkkkkkkkkkk12k1
    ..k111k1111111111111k12k1
    ..k111k1111111111111k12k1
    ..k111k1111111111111k11k1
    ...k111k111111111111k11k1
    ...k111k111111111111k11k1
    ...k111k11111111111111111
    ....k111k1111111111k1111.
    ....k111k111111111k1.....
    .....k111k111111kk1......
    .....k111k111kkkk1.......
    ......k111k1kkk1.........
    .......1111k1............
    ........1111.............
    .........................
  `),
  37,
  40,
];

const DURAG: Worn = {
  worn: true,
  front: DURAG_FRONT,
  ribbon: DURAG_TIES,
  dead: DURAG_DEAD,
};

/**
 * The turban on the head (ADR-0089), over the face in the hair's place (at (4, 8)): a Rajasthani pagri, wrapped layers
 * with volume and folds, the layers on the side of the head rising to the front and the front's crossing them, its
 * dominant wrap in the team's colour dotted like bandhani (tie-dye: white dots), one layer green and one orange with
 * yellow dots; the loose end hangs at the back (`TURBAN_TAIL`, the ribbon's place).
 */
const TURBAN_FRONT: Part = [
  grid(`
    .................cccccccccccccbbbb................
    ..............cccbbbBBcccbbbwbBbbbbbb.............
    ...........cccbbbBBBccbbbbbbwbBBBbbbbbbb..........
    .........ccbbbbBBcccbbbbwbbbbbBccBBbbbwbbb........
    ........cbbbBBBccbbbbbbbbbbBBBBbbccBbbbbbbb.......
    ......ccbbBBcccbbbbbwbbbbBBlllgBbbccBBbbbbbbb.....
    .....cbBBBccbbbbwbbbbbBBBllggggBwbbbccBBbbwbbb....
    ....cBBcccbbbbbbbbbbBBlllgggggyBbbbbbbccBbbbbbb...
    ....cccbbbbbwbbbbBBBllggggyggggBBbbbwbbccBBbbbb...
    ...bcbbbwbbbbbbBBlllggggggggggGBoBBbbbbbbccBBbbb..
    ...bbbbbwbbbBBBllgggggyggggGGGccBooBbbbbbbbccBbb..
    ...bwbbbbbBBlllgggyggggggGGcccbbBUooBBbbwbbbccBB..
    ..bbbbbBBBllggggggygggGGGccbbbbbBUUUooBBbbbbbbccb.
    ...bbBBlllggggygggggGGcccbbbwbbbBUyUUUooBbbbwbbb..
    ...bBllggggggggggGGGccbbbbbbwbbbBUUUUUUooBBbbbbB..
    ...llgggggyggggGGcccbbbbwbbbbbBBcBuUUUyUUooBBBBB..
    ....ggygggggGGGccbbbbbbbbbbBBBccbBcuUUUUUUuUUBB...
    ....ggggggGGcccbbbbbwbbbbBBcccbbbBccuuUuuuuuUUB...
    ....gggGGGccbbbbwbbbbbBBBccbbbbbbBbbcbuuuuuuuu....
    ....gGGcccbbbbbbbbbbBBcccbbbbbwbbBBBBBbbuuuBu.....
    ....GccbbbbbwbbbbBBBccbbbbwbbbbbBBBBBBBbbuB.......
    ....cbbbwbbbbbBBBBccbbbbbbbbbBBBbbBBBBBBBB........
    .....bbbwbbbBB....BBBBBBBBBBBBBBBBBB..............
    .....bbbbbBB......................................
    ......bBBB........................................
  `),
  4,
  8,
];

/** A turban's loose end (frame points), as a ribbon part: team cloth with bandhani dots. */
function tail(path: Path, lying = false): Part {
  const [c, x, y] = strap(path, 5, lying);
  const out = c.clone();
  for (let yy = 0; yy < c.h; yy++)
    for (let xx = 0; xx < c.w; xx++)
      if (c.get(xx, yy) === 'b' && (xx + x + 2 * (yy + y)) % 5 === 0) out.set(xx, yy, 'w');
  return [out, x, y - HAT_Y];
}

const TURBAN_TAIL: Record<RibbonKey, Part> = {
  hang: tail([
    [7, 30],
    [5, 38],
    [5, 46],
  ]),
  hang2: tail([
    [7, 30],
    [4, 38],
    [3, 45],
  ]),
  flowA: tail(
    [
      [8, 29],
      [3, 32],
      [-3, 33],
    ],
    true,
  ),
  flowB: tail(
    [
      [8, 29],
      [3, 31],
      [-3, 35],
    ],
    true,
  ),
  flowC: tail(
    [
      [8, 29],
      [3, 33],
      [-3, 32],
    ],
    true,
  ),
};

/**
 * The turban on the dead pose's head (seen from above, the crown to the right): its layers round the crown in
 * chevrons crossing along the head's middle, green over it and orange under it, its edge on the forehead in shadow, the
 * loose end fallen on the grass above.
 */
const TURBAN_DEAD: Part = [
  grid(`
    ..........................
    ..........................
    .....................ccwbb
    ....................cwbbB.
    ....................cbbbB.
    ...................cbbwB..
    ........bbBcbwBcbbBlwbbB..
    .......BbbBcbbBcbbBlgbB...
    ......BwwBcbbBcwwBlggG....
    .....BbbBcbbBcbbBlggGcb...
    .....BbbBcwbBcbbBlygGcb...
    ....BbbBcbbBcbbBlggGcbbB..
    ....BwbBcbbBcwbBlggGcwbB..
    ...BbbBcbbBcbbBlggGcbbBcb.
    ...BbBcbwBcbbBlgyGcbbBcbw.
    ...BbBcbbBcbbBlggGcbbBcbb.
    ...BBcbbBcbwBlggGcbwBcbbB.
    ...BBcbbBcbbBlggGcbbBcbbB.
    ..BBcwBBBBBBBBBBBBBBBBBBBB
    ...BBcbbBoUUucbbBcbbBcbbB.
    ...BBcbbBoUyucbbBcbwBcbbB.
    ...BbBcbbBoUUucbbBcbbBcbb.
    ...BbBcbwBoUUucbwBcbbBcbw.
    ...BbbBcbbBoUUucbbBcbbBcb.
    ....BwbBcbbBoyUucbbBcwbB..
    ....BbbBcbbBoUUucbbBcbbB..
    .....BbbBbwbBUUUubwbBbb...
    .....BbbBbbbBUUUubbbBbb...
    ......BBBBbBBBUuuubBBB....
    .......BBBBbBBBUuuubB.....
    ........BBBbBBBUuuub......
    ..........BBbBBBUu........
    ..........................
  `),
  36,
  38,
];

const TURBAN: Worn = { worn: true, front: TURBAN_FRONT, ribbon: TURBAN_TAIL, dead: TURBAN_DEAD };

export const HEADWEAR = { conical: CONICAL, durag: DURAG, turban: TURBAN } satisfies Record<string, Headwear>;
